import {
  AdditionalInputValueInput,
  CreditRequest_OfflineDataQuery,
  ItemsContext_MultiOrderGuideItemsQuery,
  NewOrder_OrderContextQuery,
  NewOrder_OrderFieldsFragment,
  NewOrder_VisibleVariantPackQuery,
  PriceEditing_GetPriceEditDisplayInfoQuery,
  UserContext_EmployeeQuery,
  VariantPackInfoContext_OfflineInfoListQuery,
  VariantPackInfoContext_OrderConstraintsQuery,
} from '@/types';
import {
  BatchTagPrintingData,
  OfflineAuthAdapter,
  OfflineBatchTagPrintingAdapter,
  OfflineCreditRequestDataAdapter,
  OfflineDataStatusAdapter,
  OfflineDataStatusMap,
  OfflineOrderConstraintsAdapter,
  OfflineOrderContextAdapter,
  OfflineOrderContextAdapterEvent,
  OfflineOrderData,
  OfflineOrderGuideAdapter,
  OfflineOrderGuidePriceEditDisplayInfoAdapter,
  OfflineQueuedOrdersAdapter,
  OfflineQueuedOrdersMap,
  OfflineRestaurantsWithQueuedOrdersAdapter,
  OfflineUserContextAdapter,
  OfflineVariantPackInfoAdapter,
  OfflineVisibleVariantPackAdapter,
  RestaurantsWithQueuedOrdersMap,
  VariantPackInfoAdapterEvent,
  VariantPacksPriceEditMap,
} from '@/adapters/offline-mode';
import { captureException } from '@/utils/sentry';
import { InputValueMap, isDefaultCart } from '@/utils/new-order';
import {
  isOfflineAuthTokenValid,
  OfflineAuthToken,
} from '@/utils/offline-mode';
import { OrderSummaryInputID } from '@/enums';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOfflineModeActive } from './use-offline-mode-active';

// QueryData is undefined when the data is loading, we've errored, or are otherwise uncertain
// QueryData is null when the data does not exist, and we are certain of that
export type QueryData<T> = T | undefined | null;

interface QueryResult<T> {
  data: QueryData<T>;
  error?: boolean;
  hasResponse: boolean;
  loading: boolean;
  refetch: () => Promise<void>;
}

interface OfflineOrderContext {
  offlineOrderContext: QueryResult<NewOrder_OrderContextQuery>;
  saveOfflineOrderContext: (
    orderContext: NewOrder_OrderContextQuery,
  ) => Promise<void>;
  syncOfflineOrderContextWithOnlineChange: (
    orderContext: NewOrder_OrderContextQuery,
  ) => Promise<void>;
}

export const useOfflineOrderContext = (
  restaurantUUID: string,
  supplierUUID: string,
): OfflineOrderContext => {
  const offlineOrderContextAdapter = useRef<OfflineOrderContextAdapter>(null);
  const [offlineOrderContext, setOfflineOrderContext] = useState<
    Omit<QueryResult<NewOrder_OrderContextQuery>, 'refetch'>
  >({
    data: undefined,
    hasResponse: false,
    loading: true,
  });

  /**
   * Saves the offline NewOrderContext while in Offline Mode.
   * Only call this function while offline, as it marks the context as unsynced.
   */
  const saveOfflineOrderContext = useCallback(
    async (orderContext: NewOrder_OrderContextQuery) => {
      const newOrderContext =
        mapOrderContextToOfflineOrderContext(orderContext);
      await offlineOrderContextAdapter.current?.save({
        newOrderContext,
        synced: false,
      });

      setOfflineOrderContext((prev) => ({
        ...prev,
        data: newOrderContext,
      }));
    },
    [],
  );

  /**
   * Saves the offline NewOrderContext while online.
   * Only call this function while online, as it marks the context as synced.
   */
  const syncOfflineOrderContextWithOnlineChange = useCallback(
    async (orderContext: NewOrder_OrderContextQuery) => {
      const pendingOfflineChanges = !(
        await offlineOrderContextAdapter.current?.fetch()
      )?.synced;

      if (!pendingOfflineChanges) {
        const newOrderContext =
          mapOrderContextToOfflineOrderContext(orderContext);
        await offlineOrderContextAdapter.current?.save({
          newOrderContext,
          synced: true,
        });

        setOfflineOrderContext((prev) => ({
          ...prev,
          data: newOrderContext,
        }));
      }
    },
    [],
  );

  const fetchOfflineOrderContext = useCallback(async () => {
    try {
      const offlineOrderContextData =
        await offlineOrderContextAdapter.current?.fetch();
      setOfflineOrderContext({
        data: offlineOrderContextData?.newOrderContext,
        hasResponse: true,
        loading: false,
      });
    } catch (e) {
      captureException(e);
      setOfflineOrderContext({
        data: null,
        error: true,
        hasResponse: false,
        loading: false,
      });
    }
  }, []);

  /**
   * Set the offlineOrderContextAdapter ref on first render.
   * This will re-mount when a new restaurant is selected.
   */
  useEffect(() => {
    offlineOrderContextAdapter.current = OfflineOrderContextAdapter.getInstance(
      restaurantUUID,
      supplierUUID,
    );

    const listenerId = offlineOrderContextAdapter.current?.addEventListener(
      (event) => {
        if (event === OfflineOrderContextAdapterEvent.Save) {
          fetchOfflineOrderContext();
        }
      },
    );

    fetchOfflineOrderContext();

    return () => {
      offlineOrderContextAdapter.current?.removeEventListener(listenerId);
    };
    // isOfflineModeActive must remain in the hook dependencies,
    // so that it's triggered when the user goes offline
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    offlineOrderContext: {
      ...offlineOrderContext,
      refetch: fetchOfflineOrderContext,
    },
    saveOfflineOrderContext,
    syncOfflineOrderContextWithOnlineChange,
  };
};

export const useOfflineOrderGuide = (
  restaurantUUID: string,
  supplierUUID: string,
): QueryResult<ItemsContext_MultiOrderGuideItemsQuery> => {
  const isOfflineModeActive = useOfflineModeActive();
  const [offlineOrderGuide, setOfflineOrderGuide] = useState<
    QueryResult<ItemsContext_MultiOrderGuideItemsQuery>
  >({
    data: undefined,
    hasResponse: false,
    loading: true,
    refetch: async () => {},
  });

  useEffect(() => {
    async function fetchOrderGuide() {
      const offlineOrderGuideAdapter = new OfflineOrderGuideAdapter(
        restaurantUUID,
        supplierUUID,
      );
      try {
        const offlineOrderGuideData = await offlineOrderGuideAdapter.fetch();
        setOfflineOrderGuide({
          data: offlineOrderGuideData,
          hasResponse: true,
          loading: false,
          refetch: async () => {},
        });
      } catch (e) {
        captureException(e);
        setOfflineOrderGuide({
          data: null,
          error: true,
          hasResponse: false,
          loading: false,
          refetch: async () => {},
        });
      }
    }
    fetchOrderGuide();
    // isOfflineModeActive must remain in the hook dependencies,
    // so that it's triggered when the user goes offline
  }, [restaurantUUID, supplierUUID, isOfflineModeActive]);

  return offlineOrderGuide;
};

export const useOfflineOrderGuidePriceEditDisplayInfo = (
  restaurantUUID: string,
  supplierUUID: string,
): QueryResult<PriceEditing_GetPriceEditDisplayInfoQuery> => {
  const [
    offlineOrderGuidePriceEditDisplayInfo,
    setOfflineOrderGuidePriceEditDisplayInfo,
  ] = useState<QueryResult<PriceEditing_GetPriceEditDisplayInfoQuery>>({
    data: undefined,
    hasResponse: false,
    loading: true,
    refetch: async () => {},
  });

  useEffect(() => {
    async function fetchOrderGuidePriceEditDisplayInfo() {
      const offlineOrderGuidePriceEditDisplayInfoAdapter =
        new OfflineOrderGuidePriceEditDisplayInfoAdapter(
          restaurantUUID,
          supplierUUID,
        );
      try {
        const offlineOrderGuidePriceEditDisplayInfoData =
          await offlineOrderGuidePriceEditDisplayInfoAdapter.fetch();
        setOfflineOrderGuidePriceEditDisplayInfo({
          data: offlineOrderGuidePriceEditDisplayInfoData,
          hasResponse: true,
          loading: false,
          refetch: async () => {},
        });
      } catch (e) {
        captureException(e);
        setOfflineOrderGuidePriceEditDisplayInfo({
          data: null,
          error: true,
          hasResponse: false,
          loading: false,
          refetch: async () => {},
        });
      }
    }
    fetchOrderGuidePriceEditDisplayInfo();
  }, [restaurantUUID, supplierUUID]);

  return offlineOrderGuidePriceEditDisplayInfo;
};

export const useOfflineUserContext = (
  userUUID: string | null,
  onlineData?: UserContext_EmployeeQuery,
) => {
  const isOfflineModeActive = useOfflineModeActive();
  const onlineDataRef = useRef(onlineData);
  onlineDataRef.current = onlineData;

  const [offlineUserContext, setOfflineUserContext] = useState<
    QueryResult<UserContext_EmployeeQuery>
  >({
    data: undefined,
    hasResponse: false,
    loading: true,
    refetch: async () => {},
  });

  useEffect(() => {
    async function fetchUserContext() {
      if (!userUUID) {
        setOfflineUserContext({
          data: null,
          hasResponse: false,
          loading: false,
          refetch: async () => {},
        });
        return;
      }

      try {
        const offlineUserContextAdapter = new OfflineUserContextAdapter(
          userUUID,
        );

        // Most up-to-date configs get saved and served as app moves to offline mode
        if (isOfflineModeActive && onlineDataRef.current) {
          await offlineUserContextAdapter.save(onlineDataRef.current);
        }

        const offlineUserContextData = await offlineUserContextAdapter.fetch();
        setOfflineUserContext({
          data: offlineUserContextData,
          hasResponse: true,
          loading: false,
          refetch: async () => {},
        });
      } catch (e) {
        captureException(e);
        setOfflineUserContext({
          data: null,
          error: true,
          hasResponse: false,
          loading: false,
          refetch: async () => {},
        });
      }
    }
    fetchUserContext();
    // isOfflineModeActive must remain in the hook dependencies,
    // so that it's triggered when the user goes offline
  }, [userUUID, isOfflineModeActive]);

  return offlineUserContext;
};

export const useOfflineAuth = () => {
  const [authToken, setAuthToken] = useState<OfflineAuthToken | null>(null);

  const fetchAuthToken = useCallback(async () => {
    try {
      const adapter = new OfflineAuthAdapter();
      setAuthToken(await adapter.fetch());
    } catch (e) {
      captureException(e);
      setAuthToken(null);
    }
  }, []);

  useEffect(() => {
    fetchAuthToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const deleteOfflineAuthToken = useCallback(() => {
    new OfflineAuthAdapter().remove();
  }, []);

  const refreshOfflineAuthToken = useCallback(async () => {
    // Create a Date for 72 hours in the future, which is how long
    // we will preserve this offline auth for
    const refreshedExpiry = new Date();
    refreshedExpiry.setHours(refreshedExpiry.getHours() + 72);
    const newToken: OfflineAuthToken = { expires: refreshedExpiry };

    // Update the offline storage with the new expiry
    const adapter = new OfflineAuthAdapter();
    await adapter.save(newToken);

    // Update the state so that the query result data reflects the new expiry
    setAuthToken(newToken);
  }, []);

  // Note that if there are multiple consumers of this hook, and one refreshes the token,
  // the other consumers will not have their state updated
  return {
    deleteOfflineAuthToken,
    isOfflineAuthTokenValid: authToken
      ? isOfflineAuthTokenValid(authToken)
      : false,
    refreshOfflineAuthToken,
  };
};

interface ExtendedDataStatusResponse extends QueryResult<OfflineDataStatusMap> {
  syncedRestaurantUUIDs: string[];
}

export const useOfflineDataStatus = (): ExtendedDataStatusResponse => {
  const [dataStatus, setDataStatus] = useState<
    Omit<QueryResult<OfflineDataStatusMap>, 'refetch'>
  >({
    data: undefined,
    hasResponse: false,
    loading: true,
  });

  const fetchDataStatus = useCallback(async () => {
    const dataStatusAdapter = new OfflineDataStatusAdapter();
    try {
      const dataStatusData = await dataStatusAdapter.fetch();
      setDataStatus({
        data: dataStatusData,
        hasResponse: true,
        loading: false,
      });
    } catch (e) {
      captureException(e);
      setDataStatus({
        data: null,
        error: true,
        hasResponse: false,
        loading: false,
      });
    }
  }, []);

  useEffect(() => {
    fetchDataStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    ...dataStatus,
    refetch: fetchDataStatus,
    syncedRestaurantUUIDs: Object.keys(dataStatus.data || {}),
  };
};

export const useOfflineVariantPackInfo = (
  restaurantUUID: string | null,
  supplierUUID: string | null,
): QueryResult<VariantPackInfoContext_OfflineInfoListQuery> => {
  const [offlineVariantPackInfo, setOfflineVariantPackInfo] = useState<
    QueryResult<VariantPackInfoContext_OfflineInfoListQuery>
  >({
    data: undefined,
    hasResponse: false,
    loading: true,
    refetch: async () => {},
  });

  const offlineVariantPackInfoAdapter =
    useRef<OfflineVariantPackInfoAdapter>(null);

  async function fetchVariantPackInfo() {
    if (!offlineVariantPackInfoAdapter.current) {
      setOfflineVariantPackInfo({
        data: null,
        hasResponse: false,
        loading: false,
        refetch: async () => {},
      });
      return;
    }

    try {
      const offlineVariantPackInfoData =
        await offlineVariantPackInfoAdapter.current.fetch();
      setOfflineVariantPackInfo({
        data: offlineVariantPackInfoData,
        hasResponse: true,
        loading: false,
        refetch: async () => {},
      });
    } catch (e) {
      captureException(e);
      setOfflineVariantPackInfo({
        data: null,
        error: true,
        hasResponse: false,
        loading: false,
        refetch: async () => {},
      });
    }
  }

  /**
   * Set the offlineVariantPackInfoAdapter ref on first render.
   * This will re-mount when a new restaurant is selected.
   *
   * The restaurantUUID and supplierUUID should always exist,
   * typing just says otherwise.
   */
  useEffect(() => {
    let listenerId: string | null = null;

    if (restaurantUUID && supplierUUID) {
      offlineVariantPackInfoAdapter.current =
        OfflineVariantPackInfoAdapter.getInstance(restaurantUUID, supplierUUID);

      // When the adapter emits a save event, refetch the variant pack info to
      // keep the offline data React state in sync with the latest save
      listenerId = offlineVariantPackInfoAdapter.current?.addEventListener(
        (event) => {
          if (event === VariantPackInfoAdapterEvent.Save) {
            fetchVariantPackInfo();
          }
        },
      );

      // Do initial fetch
      fetchVariantPackInfo();
    }

    return () => {
      if (listenerId) {
        offlineVariantPackInfoAdapter.current?.removeEventListener(listenerId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return offlineVariantPackInfo;
};

const mapOrderContextToOfflineOrderContext = (
  newOrderContext: NewOrder_OrderContextQuery,
): NewOrder_OrderContextQuery => ({
  ...newOrderContext,
  getOrders: newOrderContext.getOrders.filter(
    (entry) => !entry.order || isDefaultCart(entry.order),
  ),
});

const mapOrderToOfflineOrderData = (
  deliveryDate: string | undefined,
  order: NewOrder_OrderFieldsFragment,
  orderTotalPrice: string | null,
  restaurantUUID: string,
  supplierUUID: string,
  orderNotes: string | null,
  poNumber: string | null,
  additionalInputValues: AdditionalInputValueInput[],
  variantPacksPriceEditMapForChat: VariantPacksPriceEditMap | null,
): OfflineOrderData => {
  const variantPacksPriceEditMapAtTimeOfOrder: VariantPacksPriceEditMap =
    variantPacksPriceEditMapForChat
      ? order.orders_items
          .map((orderItem) => orderItem.variants_pack.uuid)
          .reduce<VariantPacksPriceEditMap>((acc, variantPackUUID) => {
            acc[variantPackUUID] =
              variantPacksPriceEditMapForChat[variantPackUUID];
            return acc;
          }, {})
      : {};

  return {
    additionalInputValues,
    deliveryDate,
    fulfillmentType: order.fulfillment_type,
    orderNotes,
    ordersItems: order.orders_items,
    orderTotalPrice,
    poNumber,
    restaurantUUID,
    supplierUUID,
    timeslotEnd: order.fulfillment_timeslot_end,
    timeslotStart: order.fulfillment_timeslot_start,
    variantPacksPriceEditMapAtTimeOfOrder,
  };
};

export const useOfflineQueuedOrders = () => {
  const [
    offlineRestaurantWithQueuedOrders,
    setOfflineRestaurantWithQueuedOrders,
  ] = useState<RestaurantsWithQueuedOrdersMap | null>(null);
  const [queuedOfflineOrdersMap, setQueuedOfflineOrdersMap] =
    useState<OfflineQueuedOrdersMap | null>(null);

  const fetchOfflineRestaurantWithQueuedOrders = useCallback(async () => {
    const adapter = OfflineRestaurantsWithQueuedOrdersAdapter.getInstance();
    try {
      const restaurantsWithQueuedOrders = await adapter.fetch();
      let queuedOrdersMap: OfflineQueuedOrdersMap = {};
      for (const restaurantUUID in restaurantsWithQueuedOrders) {
        const supplierUUID = restaurantsWithQueuedOrders[restaurantUUID];
        const offlineQueuedOrdersAdapter = new OfflineQueuedOrdersAdapter(
          restaurantUUID,
          supplierUUID,
        );
        const offlineQueuedOrdersMap = await offlineQueuedOrdersAdapter.fetch();
        queuedOrdersMap = { ...queuedOrdersMap, ...offlineQueuedOrdersMap };
      }
      setOfflineRestaurantWithQueuedOrders(restaurantsWithQueuedOrders);
      setQueuedOfflineOrdersMap(queuedOrdersMap);
    } catch (e) {
      captureException(e);
      setOfflineRestaurantWithQueuedOrders(null);
    }
  }, []);

  const queueOfflineOrder = async (
    deliveryDate: string | undefined,
    restaurantUUID: string,
    supplierUUID: string,
    order: NewOrder_OrderFieldsFragment,
    orderTotalPrice: string | null,
    additionalInputValueMap: InputValueMap,
    variantPacksPriceEditMapForChat: VariantPacksPriceEditMap | null,
  ) => {
    const offlineOrderContextAdapter = OfflineOrderContextAdapter.getInstance(
      restaurantUUID,
      supplierUUID,
    );
    const orderUUID = order?.uuid!;

    const offlineQueuedOrdersAdapter = new OfflineQueuedOrdersAdapter(
      restaurantUUID,
      supplierUUID,
    );
    const offlineQueuedOrdersMap = await offlineQueuedOrdersAdapter.fetch();

    const additionalInputValues = [];
    let orderNotes: string | null = null;
    let poNumber: string | null = null;

    for (const [inputID, { title, value }] of Object.entries(
      additionalInputValueMap,
    )) {
      const inputValue = value.trim();
      switch (inputID) {
        case OrderSummaryInputID.Notes:
          orderNotes = inputValue;
          break;
        case OrderSummaryInputID.PONumber:
          poNumber = inputValue;
          break;
        default:
          additionalInputValues.push({
            id: inputID,
            title,
            value: inputValue,
          });
          break;
      }
    }

    const offlineQueuedOrder: OfflineOrderData = mapOrderToOfflineOrderData(
      deliveryDate,
      order,
      orderTotalPrice,
      restaurantUUID,
      supplierUUID,
      orderNotes,
      poNumber,
      additionalInputValues,
      variantPacksPriceEditMapForChat,
    );

    await offlineQueuedOrdersAdapter.save({
      ...offlineQueuedOrdersMap,
      [orderUUID]: offlineQueuedOrder,
    });

    await offlineOrderContextAdapter.removeOrder(orderUUID);

    const offlineRestaurantsWithQueuedOrdersAdapter =
      OfflineRestaurantsWithQueuedOrdersAdapter.getInstance();
    let restaurantsWithQueuedOrders =
      await offlineRestaurantsWithQueuedOrdersAdapter.fetch();
    if (!restaurantsWithQueuedOrders) {
      restaurantsWithQueuedOrders = {};
    }

    restaurantsWithQueuedOrders[restaurantUUID] = supplierUUID;
    await offlineRestaurantsWithQueuedOrdersAdapter.save(
      restaurantsWithQueuedOrders,
    );
    fetchOfflineRestaurantWithQueuedOrders();
  };

  const removeOfflineOrder = async (
    restaurantUUID: string,
    supplierUUID: string,
    orderUUID: string,
  ) => {
    const offlineQueuedOrdersAdapter = new OfflineQueuedOrdersAdapter(
      restaurantUUID,
      supplierUUID,
    );
    const offlineQueuedOrdersMap = await offlineQueuedOrdersAdapter.fetch();
    if (!offlineQueuedOrdersMap) {
      captureException(new Error('Offline queued orders map is null'));
      return;
    }
    delete offlineQueuedOrdersMap[orderUUID];

    // If there are still orders for the restaurant, save the updated map,
    // otherwise, remove the orders key for the restaurant, and remove the restaurant
    // from the restaurants with queued orders map. If there are no more restaurants
    // with queued orders, remove the key for that as well.
    if (Object.keys(offlineQueuedOrdersMap).length > 0) {
      await offlineQueuedOrdersAdapter.save(offlineQueuedOrdersMap);
    } else {
      offlineQueuedOrdersAdapter.remove();

      const offlineRestaurantsWithQueuedOrdersAdapter =
        OfflineRestaurantsWithQueuedOrdersAdapter.getInstance();
      const restaurantsWithQueuedOrders =
        await offlineRestaurantsWithQueuedOrdersAdapter.fetch();
      if (!restaurantsWithQueuedOrders) {
        return;
      }
      delete restaurantsWithQueuedOrders[restaurantUUID];

      if (Object.keys(restaurantsWithQueuedOrders).length > 0) {
        await offlineRestaurantsWithQueuedOrdersAdapter.save(
          restaurantsWithQueuedOrders,
        );
      } else {
        offlineRestaurantsWithQueuedOrdersAdapter.remove();
      }
    }

    fetchOfflineRestaurantWithQueuedOrders();
  };

  useEffect(() => {
    fetchOfflineRestaurantWithQueuedOrders();

    const offlineRestaurantsWithQueuedOrdersAdapter =
      OfflineRestaurantsWithQueuedOrdersAdapter.getInstance();
    offlineRestaurantsWithQueuedOrdersAdapter.addEventListener(
      fetchOfflineRestaurantWithQueuedOrders,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    offlineRestaurantWithQueuedOrders,
    queuedOfflineOrdersMap,
    queueOfflineOrder,
    removeOfflineOrder,
  };
};

interface UseOfflineBatchPrintResult {
  // `tagBatchPrintData` is null both before the first read and when there is
  // nothing stored, so callers need this to tell those apart.
  loading: boolean;
  tagBatchPrintData: BatchTagPrintingData | null;
  updateTagPrintBatchData: (
    tagBatchPrintData: BatchTagPrintingData,
  ) => Promise<void>;
}

export const useOfflineTagBatchPrint = (
  restaurantUUID: string,
  supplierUUID: string,
): UseOfflineBatchPrintResult => {
  const [tagBatchPrintData, setTagBatchPrintData] =
    useState<BatchTagPrintingData | null>(null);
  const [loading, setLoading] = useState(true);

  const offlineTagBatchPrintAdapterInstance = useMemo(
    () =>
      OfflineBatchTagPrintingAdapter.getInstance(restaurantUUID, supplierUUID),
    [restaurantUUID, supplierUUID],
  );

  useEffect(() => {
    const fetchTagBatchPrintData = async () => {
      try {
        const offlineTagBatchPrintData =
          await offlineTagBatchPrintAdapterInstance.fetch();
        setTagBatchPrintData(offlineTagBatchPrintData);
      } catch (e) {
        captureException(e);
        setTagBatchPrintData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchTagBatchPrintData();

    const listenerId = offlineTagBatchPrintAdapterInstance.addEventListener(
      fetchTagBatchPrintData,
    );

    return () =>
      offlineTagBatchPrintAdapterInstance.removeEventListener(listenerId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateTagPrintBatchData = useCallback(
    async (data: BatchTagPrintingData) => {
      setTagBatchPrintData(data);
      await offlineTagBatchPrintAdapterInstance.save(data);
    },
    [offlineTagBatchPrintAdapterInstance],
  );

  return { loading, tagBatchPrintData, updateTagPrintBatchData };
};

export const useOfflineVisibleVariantPacks = (
  restaurantUUID: string,
  supplierUUID: string,
): QueryResult<NewOrder_VisibleVariantPackQuery> => {
  const isOfflineModeActive = useOfflineModeActive();
  const [offlineVisibleVariantPacks, setOfflineVisibleVariantPacks] = useState<
    QueryResult<NewOrder_VisibleVariantPackQuery>
  >({
    data: undefined,
    hasResponse: false,
    loading: true,
    refetch: async () => {},
  });

  useEffect(() => {
    const fetchVisibleVariantPacks = async () => {
      const offlineVisibleVariantPackAdapter =
        new OfflineVisibleVariantPackAdapter(restaurantUUID, supplierUUID);
      try {
        const offlineVisibleVariantPacksData =
          await offlineVisibleVariantPackAdapter.fetch();
        setOfflineVisibleVariantPacks({
          data: offlineVisibleVariantPacksData,
          hasResponse: true,
          loading: false,
          refetch: fetchVisibleVariantPacks,
        });
      } catch (e) {
        captureException(e);
        setOfflineVisibleVariantPacks({
          data: null,
          error: true,
          hasResponse: false,
          loading: false,
          refetch: fetchVisibleVariantPacks,
        });
      }
    };

    fetchVisibleVariantPacks();
  }, [isOfflineModeActive, restaurantUUID, supplierUUID]);

  return offlineVisibleVariantPacks;
};

export const useOfflineOrderConstraints = (
  restaurantUUID: string,
  supplierUUID: string,
): QueryResult<VariantPackInfoContext_OrderConstraintsQuery> => {
  const isOfflineModeActive = useOfflineModeActive();
  const [offlineOrderConstraints, setOfflineOrderConstraints] = useState<
    QueryResult<VariantPackInfoContext_OrderConstraintsQuery>
  >({
    data: undefined,
    hasResponse: false,
    loading: true,
    refetch: async () => {},
  });

  useEffect(() => {
    const fetchOrderConstraints = async () => {
      const offlineOrderConstraintsAdapter = new OfflineOrderConstraintsAdapter(
        restaurantUUID,
        supplierUUID,
      );
      try {
        const offlineOrderConstraintsData =
          await offlineOrderConstraintsAdapter.fetch();
        setOfflineOrderConstraints({
          data: offlineOrderConstraintsData,
          hasResponse: true,
          loading: false,
          refetch: fetchOrderConstraints,
        });
      } catch (e) {
        captureException(e);
        setOfflineOrderConstraints({
          data: null,
          error: true,
          hasResponse: false,
          loading: false,
          refetch: fetchOrderConstraints,
        });
      }
    };

    fetchOrderConstraints();
  }, [isOfflineModeActive, restaurantUUID, supplierUUID]);

  return offlineOrderConstraints;
};

export const useOfflineCreditRequestData = (
  restaurantUUID: string,
  supplierUUID: string,
): QueryResult<CreditRequest_OfflineDataQuery> => {
  const isOfflineModeActive = useOfflineModeActive();
  const [offlineCreditRequestData, setOfflineCreditRequestData] = useState<
    QueryResult<CreditRequest_OfflineDataQuery>
  >({
    data: undefined,
    hasResponse: false,
    loading: true,
    refetch: async () => {},
  });

  useEffect(() => {
    const fetchCreditRequestData = async () => {
      const offlineCreditRequestDataAdapter =
        new OfflineCreditRequestDataAdapter(restaurantUUID, supplierUUID);
      try {
        const offlineCreditRequestDataResult =
          await offlineCreditRequestDataAdapter.fetch();
        setOfflineCreditRequestData({
          data: offlineCreditRequestDataResult,
          hasResponse: true,
          loading: false,
          refetch: fetchCreditRequestData,
        });
      } catch (e) {
        captureException(e);
        setOfflineCreditRequestData({
          data: null,
          error: true,
          hasResponse: false,
          loading: false,
          refetch: fetchCreditRequestData,
        });
      }
    };

    fetchCreditRequestData();
  }, [isOfflineModeActive, restaurantUUID, supplierUUID]);

  return offlineCreditRequestData;
};
